// ContactTable — the raw per-contact detail table AA's "HubSpot | Contacts"
// report page ends on (reference screenshot: "Showing 500 of 3,539 Rows", a
// search box) plus, filtered to a paid SOURCE, AA's "Google Ad Leads" view
// (Date Added / First Name / Last Name / Email / source, 166 rows). Same
// one-table-serves-both-screenshots shape as DealTable.tsx — a client-side
// source filter (chips) on the same loaded rows covers both reference
// screenshots instead of shipping two separate widgets.
//
// CrmContactsFrame already owns the lifecycle/country aggregate story above
// this; this frame is the raw row list underneath it, same relationship
// DealTable has to DealPipelinePanel.
//
// Same self-contained convention as the other frames in this directory
// (types import only, no imports of sibling primitives.tsx UI helpers) —
// see DealTable.tsx's module docstring. PII (name/email/address) renders
// exactly as AA's own table does — Carl's 2026-08-09 ruling unblocked
// showing this data; this frame just needs to render it, not editorialize.

import { useMemo, useState } from "react";
import { formatLocalDate } from "@/components/client-analytics/primitives";
import type { ContactRow } from "@/types/clientAnalytics";

export interface ContactTableProps {
  rows: ContactRow[];
  /** CrmContactsSection.total, when known — drives the "Showing N of M"
   * caption's denominator so the raw-pull cap reads as a sample, not the
   * whole account. Falls back to rows.length when omitted or smaller. */
  totalContacts?: number | null;
}

// Sample data for standalone preview / storybook-style rendering. No real
// contact data — synthetic rows only, mirroring the field shapes real
// HubSpot contacts carry (frequent blanks on name/email/company/job_title).
export const demoContacts: ContactRow[] = [
  {
    contact_id: "240886645047",
    created_at: "2026-08-09",
    first_name: "Jordan",
    last_name: "Rivera",
    email: "jordan.rivera@example.com",
    company_name: null,
    job_title: null,
    address: null,
    city: "Austin",
    state: "Texas",
    zip: null,
    country: "United States",
    lifecycle_stage: "Lead",
    source: "Paid Search",
    utm_source: "google",
    utm_medium: "cpc",
    utm_campaign: "brand-search",
  },
  {
    contact_id: "240712398821",
    created_at: "2026-08-07",
    first_name: "Priya",
    last_name: "Natarajan",
    email: "priya.n@example.com",
    company_name: "Example Studios",
    job_title: "Music Supervisor",
    address: null,
    city: "Kolkata",
    state: "West Bengal",
    zip: null,
    country: "India",
    lifecycle_stage: "Marketingqualifiedlead",
    source: "Direct Traffic",
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
  },
  {
    contact_id: "239004411982",
    created_at: "2026-07-28",
    first_name: null,
    last_name: null,
    email: "anon-form@example.com",
    company_name: null,
    job_title: null,
    address: null,
    city: null,
    state: null,
    zip: null,
    country: null,
    lifecycle_stage: "Subscriber",
    source: "Organic Search",
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
  },
  {
    contact_id: "238557102394",
    created_at: "2026-07-15",
    first_name: "Morgan",
    last_name: "Lee",
    email: "morgan.lee@example.com",
    company_name: "Lee Creative LLC",
    job_title: "Owner",
    address: null,
    city: "Denver",
    state: "Colorado",
    zip: "80202",
    country: "United States",
    lifecycle_stage: "Customer",
    source: "Paid Search",
    utm_source: "google",
    utm_medium: "cpc",
    utm_campaign: "remarketing",
  },
];

// "2026-08-09" (or a full ISO timestamp) -> "Aug 9, 2026". Matches
// DealTable's formatDealDate style. Parsing goes through the shared
// primitives.parseLocalDate: `new Date("YYYY-MM-DD")` is UTC midnight per the
// ES spec, so it rendered created_at a day early in every US timezone.
export function formatContactDate(raw?: string | null): string {
  if (!raw) return "—";
  return formatLocalDate(raw) ?? raw;
}

function fullName(row: ContactRow): string {
  const parts = [row.first_name, row.last_name].filter((p): p is string => Boolean(p && p.trim()));
  return parts.length > 0 ? parts.join(" ") : "—";
}

function location(row: ContactRow): string {
  const parts = [row.city, row.state || row.country].filter((p): p is string => Boolean(p && p.trim()));
  return parts.length > 0 ? parts.join(", ") : "—";
}

export function ContactTable({ rows, totalContacts }: ContactTableProps) {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);

  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (r.source) set.add(r.source);
    }
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (sourceFilter && r.source !== sourceFilter) return false;
      if (!q) return true;
      return (
        fullName(r).toLowerCase().includes(q) ||
        (r.email ?? "").toLowerCase().includes(q) ||
        (r.company_name ?? "").toLowerCase().includes(q) ||
        (r.lifecycle_stage ?? "").toLowerCase().includes(q) ||
        (r.source ?? "").toLowerCase().includes(q) ||
        location(r).toLowerCase().includes(q)
      );
    });
  }, [rows, search, sourceFilter]);

  // Adaptive: this frame renders nothing when there's no contact-level
  // detail — callers should still render the aggregate contacts panel
  // (CrmContactsFrame) on its own.
  if (!rows || rows.length === 0) return null;

  const shown = filtered.length;
  const total = totalContacts && totalContacts > rows.length ? totalContacts : rows.length;
  // Once a filter narrows the visible set below the loaded rows, the honest
  // denominator is the loaded-row count (what the filter is actually
  // slicing), not the unfiltered account total — same convention as
  // DealTable's "Showing 11 of 11 Rows" once SOURCE-filtered.
  const denominator = search || sourceFilter ? rows.length : total;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-b border-border bg-muted/40">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
            Contacts
          </span>
          {sources.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => setSourceFilter(null)}
                className={`rounded-full px-2.5 py-0.5 text-dash-context font-semibold whitespace-nowrap ${
                  sourceFilter === null
                    ? "bg-coral text-white"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                All sources
              </button>
              {sources.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSourceFilter(sourceFilter === s ? null : s)}
                  className={`rounded-full px-2.5 py-0.5 text-dash-context font-semibold whitespace-nowrap ${
                    sourceFilter === s
                      ? "bg-coral text-white"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  title={s}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts…"
            className="rounded-md border border-border bg-background px-2.5 py-1 text-dash-context text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <div className="text-dash-context text-muted-foreground tabular whitespace-nowrap">
            Showing {shown.toLocaleString()} of {denominator.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full border-collapse text-dash-subtitle">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Date added
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Name
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Email
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Company
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Location
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Lifecycle stage
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Source
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr
                key={row.contact_id || `${row.email}-${i}`}
                className={`border-b border-border last:border-0 transition-colors hover:bg-muted/60 ${
                  i % 2 === 1 ? "bg-muted/30" : "bg-card"
                }`}
              >
                <td className="px-4 py-2 whitespace-nowrap tabular text-muted-foreground">
                  {formatContactDate(row.created_at)}
                </td>
                <td className="px-4 py-2 max-w-[180px] truncate text-foreground" title={fullName(row)}>
                  {fullName(row)}
                </td>
                <td className="px-4 py-2 max-w-[220px] truncate text-muted-foreground" title={row.email ?? undefined}>
                  {row.email ?? "—"}
                </td>
                <td
                  className="px-4 py-2 max-w-[160px] truncate text-muted-foreground"
                  title={row.company_name ?? undefined}
                >
                  {row.company_name ?? "—"}
                </td>
                <td className="px-4 py-2 max-w-[160px] truncate text-muted-foreground" title={location(row)}>
                  {location(row)}
                </td>
                <td className="px-4 py-2 max-w-[140px] truncate text-muted-foreground" title={row.lifecycle_stage ?? undefined}>
                  {row.lifecycle_stage ?? "—"}
                </td>
                <td className="px-4 py-2 max-w-[140px] truncate text-muted-foreground" title={row.source ?? undefined}>
                  {row.source ?? "—"}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-dash-context text-muted-foreground">
                  No contacts match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
